/* The toast viewport is the only portaled layer with a z-index, so toasts stay above dialogs. */
.viewport {
  position: fixed;
  inset-block-end: var(--space-4);
  inset-inline: var(--space-4);
  z-index: var(--z-toast);
  margin-inline-start: auto;
  inline-size: auto;
  max-inline-size: var(--toast-width);
}

@media (width < 40rem) {
  .viewport {
    max-inline-size: none;
  }
}

.toast {
  --gap: var(--space-2);
  --peek: var(--space-2);
  --scale: calc(max(0, 1 - (var(--toast-index) * 0.05)));
  --shrink: calc(1 - var(--scale));
  --height: var(--toast-frontmost-height, var(--toast-height));
  --offset-y: calc(var(--toast-offset-y) * -1 + (var(--toast-index) * var(--gap) * -1) + var(--toast-swipe-movement-y));

  position: absolute;
  inset-block-end: 0;
  inset-inline-end: 0;
  box-sizing: border-box;
  inline-size: 100%;
  block-size: var(--height);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-raised);
  color: var(--color-text);
  box-shadow: var(--shadow-popover);
  transform-origin: bottom center;
  transform: translateX(var(--toast-swipe-movement-x))
    translateY(
      calc(var(--toast-swipe-movement-y) - (var(--toast-index) * var(--peek)) - (var(--shrink) * var(--height)))
    )
    scale(var(--scale));
  user-select: none;
  transition:
    transform var(--duration-slow) var(--easing-standard),
    opacity var(--duration-slow) var(--easing-standard),
    block-size var(--duration-fast) ease;
}

.toast[data-expanded] {
  block-size: var(--toast-height);
  transform: translateX(var(--toast-swipe-movement-x)) translateY(var(--offset-y));
}

.toast[data-starting-style],
.toast[data-ending-style] {
  transform: translateY(150%);
}

.toast[data-limited] {
  opacity: 0;
}

.toast[data-ending-style] {
  opacity: 0;
}

.toast[data-ending-style][data-swipe-direction="right"] {
  transform: translateX(calc(var(--toast-swipe-movement-x) + 150%)) translateY(var(--offset-y));
}

.toast[data-ending-style][data-swipe-direction="down"] {
  transform: translateY(calc(var(--toast-swipe-movement-y) + 150%));
}

/* Keeps the hover state while the pointer crosses the gap between stacked toasts. */
.toast::after {
  position: absolute;
  inset-block-start: 100%;
  inset-inline-start: 0;
  inline-size: 100%;
  block-size: calc(var(--gap) + 1px);
  content: "";
}

.content {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
  padding: var(--space-3);
  overflow: hidden;
  transition: opacity var(--duration-normal) var(--easing-standard);
}

.content[data-behind] {
  opacity: 0;
}

.content[data-expanded] {
  opacity: 1;
}

.icon {
  display: inline-flex;
  padding-block-start: calc(var(--space-1) / 2);
  color: var(--color-text-muted);
}

.toast[data-tone="success"] .icon {
  color: var(--color-success);
}

.toast[data-tone="warning"] .icon {
  color: var(--color-warning);
}

.toast[data-tone="danger"] .icon {
  color: var(--color-danger);
}

.toast[data-tone="info"] .icon {
  color: var(--color-info);
}

.text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 0;
}

.title {
  font-size: var(--text-md);
  font-weight: var(--weight-medium);
  line-height: var(--leading-tight);
}

.description {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.action {
  flex: none;
  block-size: var(--control-sm);
  padding-inline: var(--space-2);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
}

@media (hover: hover) and (pointer: fine) {
  .action:hover {
    background: var(--color-surface-hover);
  }
}
